import { BaseKey } from '@refinedev/core';
import { message } from 'antd';
import { AxiosInstance } from 'axios';
import { Dayjs } from 'dayjs';

import { PACKAGE_URLS } from '@/constants/urls';
import FileData from '@/model/General/FileData';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Reception } from '@/model/Logistic/Reception/Reception';

class ReceptionApi {
  axiosInstance: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  uploadFiles(files: FileData[], packageId: BaseKey): Promise<any[]> {
    if (!files || files.length === 0) {
      return Promise.resolve([]);
    } else {
      const uploadPromises: Promise<any>[] = [];

      files.forEach(file => {
        if (!file.blobFile) {
          return;
        }

        const formData = new FormData();

        formData.append('photo', file.blobFile);
        formData.append('package', String(packageId));

        uploadPromises.push(
          this.axiosInstance
            .post(`${PACKAGE_URLS.PHOTOS}/`, formData)
            .then(response => {
              return response.data;
            })
            .catch(error => {
              message.error(error.response?.data?.message || 'Error uploading file');

              console.error(`Error uploading file ${file.title}:`, error);
              throw error;
            }),
        );
      });

      return Promise.all(uploadPromises);
    }
  }

  removeFiles(files: FileData[]): Promise<any[]> {
    if (!files || files.length === 0) {
      return Promise.resolve([]);
    } else {
      const removePromises: Promise<any>[] = [];

      files.forEach(file => {
        removePromises.push(
          this.axiosInstance
            .delete(`${PACKAGE_URLS.PHOTOS}/${file.id}`)
            .then(response => {
              return response.data;
            })
            .catch(error => {
              console.error(`Error removing file ${file.title}:`, error);
              throw error;
            }),
        );
      });

      return Promise.all(removePromises);
    }
  }

  async updateReceptionState(receptionId: number, newState: PACKAGE_STATES, date?: Dayjs) {
    try {
      const response = await this.axiosInstance.patch(`/receptions/${receptionId}`, {
        status: newState,
        date: date ? date : undefined,
        active_claim: false,
      });

      return response.data;
    } catch (error) {
      console.error('Error updating reception state:', error);
      throw error;
    }
  }

  async updateReceptionShipper(receptionId: number, shipperId: number) {
    try {
      const response = await this.axiosInstance.patch(`/receptions/${receptionId}`, {
        shipper: shipperId,
        active_claim: false,
      });

      return response.data;
    } catch (error) {
      console.error('Error updating reception shipper:', error);
      throw error;
    }
  }
  async updateReceptionTechnician(receptionId: number, technicianId?: number) {
    try {
      const response = await this.axiosInstance.patch(`/receptions/${receptionId}`, {
        technician: technicianId ? technicianId : null,
        active_claim: false,
      });

      return response.data;
    } catch (error) {
      console.error('Error updating reception technician:', error);
      throw error;
    }
  }

  async updateReception(receptionId: number, receptionData: Partial<Reception>) {
    try {
      const response = await this.axiosInstance.patch(`/receptions/${receptionId}`, {
        ...receptionData,
        active_claim: false,
      });

      return response.data;
    } catch (error) {
      console.error('Error updating reception tracking number:', error);
      throw error;
    }
  }

  async sendClaimEmail(receptionId: number, emails: string) {
    try {
      const response = await this.axiosInstance.get(`/send_claim_email`, {
        params: {
          email: emails,
          tipo: 1,
          paquete_id: receptionId,
        },
      });

      return response;
    } catch (error) {
      console.error('Error sending claim email:', error);
    }
  }
}

export default ReceptionApi;
